<template>
  <div class="container-fluid mt-4 bg-white px-0">
    <chart-header
      :title="$t('flexigo.dashboard.onTimePerformance')"
      :has-data="!!otpItems.length"
      @filter-change="onFilterChange"
      @download="download"
    />
    <sub-chart-header
      @vehicle-change="onVehicleChange"
      @driver-change="onDriverChange"
      @threshold-ready="onThresholdReady"
      @threshold-change="onThresholdChange"
    />
    <hr />
    <div class="px-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="chart-left-title">{{ $t('flexigo.dashboard.trips') }}</div>
        <div class="chart-right-title">{{ $t('flexigo.dashboard.otpPercent') }}</div>
      </div>
      <highcharts ref="otpChart" :options="chartOptions"></highcharts>
    </div>
    <excel-downloader ref="excelDownloader" :data="excelData" :file-name="'OTP_Chart'" :sheet-name="'OTP'" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { format } from 'date-fns';
import { Chart } from 'highcharts-vue';
import {
  getXAxisFormatType,
  formatXAxisLabel,
  groupDataByFormatType,
  aggregateGroupedData,
  sortHourlyItemsByWindow,
} from '@/modules/Flexigo/helpers/chartXAxisFormatter';
import { exportChartAsPNG } from '@/modules/Flexigo/components/dashboard/otp/utils/chartExport';
import chartTooltipPositioner from '@/modules/Flexigo/helpers/chartTooltipPositioner';
import buildGapPlotLines from '@/modules/Flexigo/helpers/chartGapPlotLines';
import chartHeader from './ChartHeader.vue';
import subChartHeader from './SubChartHeader.vue';
import ExcelDownloader from './ExcelDownloader.vue';

/**
 * OTP is undefined for a bucket with no finished trips — neither 0% nor the 100% older
 * backends still report there. Null it out before aggregating so the day's average skips
 * such hours and the line breaks instead of drawing a value (VSER-14215). Rows that don't
 * carry a trip count are left untouched.
 */
function withoutOtpOnEmptyBucket(item) {
  if (item?.trips !== 0) return item;
  return { ...item, pickupOtp: null, dropoffOtp: null };
}

export default {
  name: 'OtpChart',
  components: {
    chartHeader,
    subChartHeader,
    highcharts: Chart,
    ExcelDownloader,
  },
  data() {
    return {
      filters: {
        startDate: null,
        endDate: null,
        fromHour: null,
        toHour: null,
        vehicleIds: [],
        driverIds: [],
        threshold: undefined,
      },
      selectedDate: 'thisweek',
    };
  },
  computed: {
    ...mapGetters('onDemandDashboard', {
      otpItems: 'otpItems',
    }),
    xAxisFormatType() {
      return getXAxisFormatType(this.filters.startDate, this.filters.endDate, this.selectedDate);
    },
    // Items as the chart/excel/gap-markers all see them: raw hourly rows (with OTP nulled on
    // empty buckets so aggregation skips them, VSER-14215) or grouped+aggregated otherwise.
    processedItems() {
      const items = (this.otpItems || []).map(withoutOtpOnEmptyBucket);
      if (items.length === 0) return items;
      const formatType = this.xAxisFormatType;
      // Overnight operation windows wrap next-day early hours under the same operationDay;
      // reorder so 00:00 lands after 23:00 instead of before 20:00.
      if (formatType === 'hourly') {
        return sortHourlyItemsByWindow(items, formatType, this.filters.fromHour, this.filters.toHour);
      }
      const grouped = groupDataByFormatType(items, formatType);
      return Object.values(grouped).map((groupedItems) =>
        aggregateGroupedData(
          groupedItems,
          ['delayedPickups', 'delayedDropoffs', 'trips'], // Sum
          ['pickupOtp', 'pickupAvgDelay', 'dropoffAvgDelay'], // Average
          ['pickupMaxDelay', 'dropoffMaxDelay'], // Max
          ['dropoffOtp'], // Weighted avg by trips
        ),
      );
    },
    // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip over
    // empty buckets. Replaces the old `...` placeholder-bar approach (fillDateGaps) which
    // showed a visual break but didn't name the duration.
    gapPlotLines() {
      return buildGapPlotLines(this.processedItems, this.xAxisFormatType);
    },
    excelData() {
      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Determine column header based on formatType
      const firstColumnHeader =
        formatType === 'hourly'
          ? this.$t('flexigo.dashboard.hour') || 'Hour'
          : this.$t('flexigo.dashboard.date') || 'Date';

      return items.map((item) => {
        const label = formatXAxisLabel(item, formatType, this.$i18n.locale);
        const pickupOtp = item?.pickupOtp || 0;
        const dropoffOtp = item?.dropoffOtp || 0;
        const pickupAvgDelay = item?.pickupAvgDelay || 0;
        const pickupMaxDelay = item?.pickupMaxDelay || 0;
        const dropoffAvgDelay = item?.dropoffAvgDelay || 0;
        const dropoffMaxDelay = item?.dropoffMaxDelay || 0;

        return {
          [firstColumnHeader]: label,
          [this.$t('flexigo.dashboard.avgPickupDelay')]: pickupAvgDelay,
          [this.$t('flexigo.dashboard.maxPickupDelay')]: pickupMaxDelay,
          [this.$t('flexigo.dashboard.delayedPickups')]: item?.delayedPickups || 0,
          [this.$t('flexigo.dashboard.pickupOtp')]: pickupOtp % 1 === 0 ? pickupOtp : Number(pickupOtp.toFixed(1)),
          [this.$t('flexigo.dashboard.avgDropoffDelay')]: dropoffAvgDelay,
          [this.$t('flexigo.dashboard.maxDropoffDelay')]: dropoffMaxDelay,
          [this.$t('flexigo.dashboard.delayedDropoffs')]: item?.delayedDropoffs || 0,
          [this.$t('flexigo.dashboard.dropoffOtp')]: dropoffOtp % 1 === 0 ? dropoffOtp : Number(dropoffOtp.toFixed(1)),
        };
      });
    },
    chartOptions() {
      const vm = this;
      const chartOptions = {
        chart: {
          type: 'column',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          crosshair: true,
          // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip
          // over empty buckets (mirrors Shuttle RidershipByRouteChart).
          plotLines: this.gapPlotLines,
        },
        yAxis: [
          {
            title: { text: 'Trips', align: 'high', rotation: 0, offset: 0, y: -15 },
            min: 0,
            tickInterval: 3,
            endOnTick: false,
            gridLineColor: '#f0f2f5',
          },
          {
            title: { text: 'OTP (%)', align: 'high', rotation: 0, offset: 0, y: -15 },
            min: 0,
            max: 100,
            tickInterval: 10,
            endOnTick: false,
            opposite: true,
            gridLineWidth: 0,
          },
        ],
        plotOptions: {
          column: {
            stacking: 'normal', // Mor ve turuncu barları üst üste yığar
            borderWidth: 0,
            pointWidth: 12, // Barların görseldeki gibi ince durması için
            pointPlacement: 'on',
          },
          spline: {
            lineWidth: 2,
            marker: {
              fillColor: '#ffffff', // Çizgi üzerindeki noktaların içinin beyaz olması için
              lineWidth: 2,
              radius: 5,
            },
          },
        },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#363647',
          style: { color: '#ffffff' },
          borderRadius: 6,
          positioner(labelWidth, labelHeight, point) {
            return chartTooltipPositioner(labelWidth, labelHeight, point, this.chart);
          },
          formatter() {
            // Get data from the first point (all points share the same x-axis data)
            const pointData = this.points[0]?.point?.options;

            // Extract metrics from point data
            const pickupAvgDelay = pointData?.pickupAvgDelay || 0;
            const pickupMaxDelay = pointData?.pickupMaxDelay || 0;
            const delayedPickups = pointData?.delayedPickups || 0;
            const pickupOtp = pointData?.pickupOtp ?? null;
            const dropoffAvgDelay = pointData?.dropoffAvgDelay || 0;
            const dropoffMaxDelay = pointData?.dropoffMaxDelay || 0;
            const delayedDropoffs = pointData?.delayedDropoffs || 0;
            const dropoffOtp = pointData?.dropoffOtp ?? null;
            // null = no finished trips in this bucket, so there is no OTP to show.
            const formatOtp = (otp) => (otp == null ? 'N/A' : `${otp % 1 === 0 ? otp : otp.toFixed(1)}%`);
            let tooltipHtml = `<div class="dashboard-graph-tooltip" style="min-width: 300px;"><div class="title">${this.x}</div>`;

            // Avg. Pick-up Delay
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.avgPickupDelay')}</span>
                <span class="text-value" style="text-align: right;">${pickupAvgDelay} min</span>
              </div>
            `;

            // Max. Pick-up Delay
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.maxPickupDelay')}</span>
                <span class="text-value" style="text-align: right;">${pickupMaxDelay} min</span>
              </div>
            `;

            // Delayed Pick-ups
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.delayedPickups')}</span>
                <span class="text-value" style="text-align: right;">${delayedPickups}</span>
              </div>
            `;

            // Pickup OTP
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.pickupOtp')}</span>
                <span class="text-value" style="text-align: right;">${formatOtp(pickupOtp)}</span>
              </div>
            `;

            // Avg. Drop-off Delay
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.avgDropoffDelay')}</span>
                <span class="text-value" style="text-align: right;">${dropoffAvgDelay} min</span>
              </div>
            `;

            // Max. Drop-off Delay
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.maxDropoffDelay')}</span>
                <span class="text-value" style="text-align: right;">${dropoffMaxDelay} min</span>
              </div>
            `;

            // Delayed Drop-offs
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.delayedDropoffs')}</span>
                <span class="text-value" style="text-align: right;">${delayedDropoffs}</span>
              </div>
            `;

            // Drop-off OTP
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.dropoffOtp')}</span>
                <span class="text-value" style="text-align: right;">${formatOtp(dropoffOtp)}</span>
              </div>
            `;

            tooltipHtml += '</div>';
            return tooltipHtml;
          },
        },
        legend: {
          squareSymbol: true,
          symbolRadius: 0,
          useHTML: true,
          margin: 30,
          zIndex: 1,
          labelFormatter() {
            return `<div style="color: #687692; font-weight: normal;">${this.name}</div>`;
          },
        },
        series: [
          {
            name: this.$t('flexigo.dashboard.delayedPickups'),
            type: 'column',
            yAxis: 0,
            color: '#a55eea',
            data: [],
          },
          {
            name: this.$t('flexigo.dashboard.delayedDropoffs'),
            type: 'column',
            yAxis: 0,
            color: '#ff9f43',
            data: [],
          },
          {
            name: this.$t('flexigo.dashboard.pickupOtp'),
            type: 'spline',
            yAxis: 1,
            color: '#26de81',
            data: [],
            marker: { lineColor: '#26de81' },
          },
          {
            name: this.$t('flexigo.dashboard.dropoffOtp'),
            type: 'spline',
            yAxis: 1,
            color: '#4b7bec',
            data: [],
            marker: {
              lineWidth: 2,
              lineColor: '#3498db',
              radius: 5,
            },
          },
        ],
      };

      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Format X-axis labels
      chartOptions.xAxis.categories = items.map((item) => formatXAxisLabel(item, formatType, this.$i18n.locale));

      // Every series carries the bucket's full metric set so the shared tooltip can read it
      // off whichever point it lands on. OTP stays null (not 0) where there is none, so the
      // spline breaks on buckets without finished trips.
      const bucketMetrics = (item) => ({
        delayedPickups: item?.delayedPickups || 0,
        delayedDropoffs: item?.delayedDropoffs || 0,
        pickupAvgDelay: item?.pickupAvgDelay || 0,
        pickupMaxDelay: item?.pickupMaxDelay || 0,
        dropoffAvgDelay: item?.dropoffAvgDelay || 0,
        dropoffMaxDelay: item?.dropoffMaxDelay || 0,
        pickupOtp: item?.pickupOtp ?? null,
        dropoffOtp: item?.dropoffOtp ?? null,
      });

      chartOptions.series[0].data = items.map((item) => ({ y: item?.delayedPickups || 0, ...bucketMetrics(item) }));
      chartOptions.series[1].data = items.map((item) => ({ y: item?.delayedDropoffs || 0, ...bucketMetrics(item) }));
      chartOptions.series[2].data = items.map((item) => ({ y: item?.pickupOtp ?? null, ...bucketMetrics(item) }));
      chartOptions.series[3].data = items.map((item) => ({ y: item?.dropoffOtp ?? null, ...bucketMetrics(item) }));

      return chartOptions;
    },
  },
  methods: {
    ...mapActions('onDemandDashboard', ['fetchOtpData']),

    download(type) {
      if (type === 'image') {
        this.downloadChartAsImage();
        return;
      }
      this.$refs.excelDownloader.download();
    },
    downloadChartAsImage() {
      const chartInstance = this.$refs.otpChart?.chart;

      exportChartAsPNG(chartInstance, `OTP_Chart_${format(new Date(), 'yyyy-MM-dd')}`, {
        width: 1200,
        height: 600,
        scale: 2,
        surface: 'otp',
      });
    },
    async onFilterChange({ startDate, endDate, fromHour, toHour, selectedDate }) {
      this.selectedDate = selectedDate;
      this.filters.startDate = startDate;
      this.filters.endDate = endDate;
      this.filters.fromHour = fromHour;
      this.filters.toHour = toHour;

      // Don't fetch if threshold is not initialized yet
      // onThresholdReady will trigger the initial fetch
      if (this.filters.threshold === undefined) {
        return;
      }

      await this.fetchOtpData({
        startDate,
        endDate,
        fromHour,
        toHour,
        vehicleIds: this.filters.vehicleIds,
        driverIds: this.filters.driverIds,
        threshold: this.filters.threshold,
      });
    },
    async onVehicleChange(vehicle) {
      this.filters.vehicleIds = vehicle ? [vehicle.id] : [];

      await this.fetchOtpData({
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        fromHour: this.filters.fromHour,
        toHour: this.filters.toHour,
        vehicleIds: this.filters.vehicleIds,
        driverIds: this.filters.driverIds,
        threshold: this.filters.threshold,
      });
    },
    async onDriverChange(driver) {
      this.filters.driverIds = driver ? [driver.id] : [];

      await this.fetchOtpData({
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        fromHour: this.filters.fromHour,
        toHour: this.filters.toHour,
        vehicleIds: this.filters.vehicleIds,
        driverIds: this.filters.driverIds,
        threshold: this.filters.threshold,
      });
    },

    async onThresholdReady(threshold) {
      this.filters.threshold = threshold;

      // Trigger initial fetch with current filters now that threshold is ready
      await this.fetchOtpData({
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        fromHour: this.filters.fromHour,
        toHour: this.filters.toHour,
        vehicleIds: this.filters.vehicleIds,
        driverIds: this.filters.driverIds,
        threshold: this.filters.threshold,
      });
    },

    async onThresholdChange(threshold) {
      this.filters.threshold = threshold;

      await this.fetchOtpData({
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        fromHour: this.filters.fromHour,
        toHour: this.filters.toHour,
        vehicleIds: this.filters.vehicleIds,
        driverIds: this.filters.driverIds,
        threshold: this.filters.threshold,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-left-title {
  color: #363647;
}
.chart-right-title {
  color: #363647;
}
</style>
